import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { sanitizeDescription } from '@/lib/sanitizeDescription';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import {
  ChevronLeft, ChevronRight, MapPin, Loader2,
  Upload, X, ArrowLeft, Pencil, Trash2, Save,
  Star, Plus, Check, BedDouble, Bath, Video, Image, Bold, Type,
  Wifi, Snowflake, UtensilsCrossed, WashingMachine, Wind, Tv, Waves,
  Bath as BathIcon, Car, Dumbbell, ArrowUpDown, Fence, Umbrella, PawPrint, Coffee,
  Sparkles, Shirt, Flame, ShieldCheck, Laptop, GripVertical, CalendarDays, Mail, Home as HomeIcon
} from 'lucide-react';
import { LeadCaptureForm } from '@/components/listings/LeadCaptureForm';
import { AmenityPicker } from '@/components/listings/AmenityPicker';
import { PhotoManagerDialog } from '@/components/listings/PhotoManagerDialog';
import { GoogleMapView } from '@/components/GoogleMapView';
import { geocodeAddress } from '@/lib/googleMaps';
import { CollectionHeader, ContactFooter, NextAvailableChip } from '@/pages/CollectionPages';

const API = process.env.REACT_APP_BACKEND_URL;
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const PLACEHOLDER_IMAGES = [
  'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1200&h=900&fit=crop&q=90',
  'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=1200&h=900&fit=crop&q=90',
  'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1200&h=900&fit=crop&q=90',
];

const AMENITY_ICONS = {
  'wifi': Wifi, 'snowflake': Snowflake, 'utensils-crossed': UtensilsCrossed,
  'washing-machine': WashingMachine, 'wind': Wind, 'tv': Tv, 'waves': Waves,
  'bath': BathIcon, 'car': Car, 'dumbbell': Dumbbell, 'arrow-up-down': ArrowUpDown,
  'fence': Fence, 'umbrella': Umbrella, 'paw-print': PawPrint, 'coffee': Coffee,
  'sparkles': Sparkles, 'shirt': Shirt, 'flame': Flame, 'shield-check': ShieldCheck,
  'laptop': Laptop,
};

function AmenityIcon({ icon, className = "h-5 w-5" }) {
  const IconComp = AMENITY_ICONS[icon] || Sparkles;
  return <IconComp className={className} />;
}



function getNext18Months() {
  const today = new Date();
  const months = [];
  for (let i = 0; i < 18; i++) {
    let m = today.getMonth() + 1 + i;
    let y = today.getFullYear();
    while (m > 12) { m -= 12; y++; }
    months.push({ year: y, month: m });
  }
  return months;
}

/* ── Rich Text Editor ─────────────────────────────────────────── */
function RichTextEditor({ value, onChange }) {
  const editorRef = useRef(null);
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current && editorRef.current) {
      editorRef.current.innerHTML = sanitizeDescription(value || '');
      isInitialMount.current = false;
    }
  }, [value]);
  const handleInput = () => { if (editorRef.current) onChange(editorRef.current.innerHTML); };
  const execCmd = (cmd) => { document.execCommand(cmd, false, null); editorRef.current?.focus(); handleInput(); };
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border bg-muted/30">
        <button type="button" onClick={() => execCmd('bold')} className="p-1.5 rounded hover:bg-muted transition-colors" title="Bold"><Bold className="h-4 w-4" /></button>
        <div className="w-px h-5 bg-border mx-1" />
        <button type="button" onClick={() => execCmd('insertParagraph')} className="p-1.5 rounded hover:bg-muted transition-colors" title="New Paragraph"><Type className="h-4 w-4" /></button>
      </div>
      <div ref={editorRef} contentEditable onInput={handleInput} className="min-h-[120px] max-h-[300px] overflow-y-auto p-3 text-sm text-muted-foreground leading-relaxed focus:outline-none whitespace-pre-wrap [&_*]:text-inherit [&_strong]:font-semibold [&_b]:font-semibold [&_p]:mb-2 [&_p:last-child]:mb-0" data-testid="rich-text-editor" />
    </div>
  );
}

function DescriptionDisplay({ html }) {
  if (!html) return null;
  // `whitespace-pre-wrap` preserves leading spaces / indents and explicit \n.
  // `[&_*]:text-inherit` forces every nested element (strong, p, h, a, li…) to
  // use the same muted color as the wrapper, so the text never jumps darker
  // mid-description when formatting changes.
  return (
    <div
      className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap [&_*]:text-inherit [&_strong]:font-semibold [&_b]:font-semibold [&_p]:mb-2 [&_p:last-child]:mb-0"
      dangerouslySetInnerHTML={{ __html: sanitizeDescription(html) }}
      data-testid="listing-description"
    />
  );
}

/* ── Map Component (z-index safe) ──────────────────────────────── */
function LocationMap({ lat, lng, address, className = '' }) {
  if (!lat || !lng) return null;
  return (
    <div className={`rounded-xl overflow-hidden border border-border/70 ${className}`} style={{ position: 'relative', zIndex: 0 }} data-testid="location-map">
      <GoogleMapView lat={lat} lng={lng} zoom={14} title={address} />
    </div>
  );
}

/* ── Photo Lightbox ────────────────────────────────────────────── */
function PhotoLightbox({ open, onOpenChange, photos, startIdx = 0 }) {
  const [idx, setIdx] = useState(startIdx);
  useEffect(() => { if (open) setIdx(startIdx); }, [open, startIdx]);
  if (!photos?.length) return null;
  const goPrev = () => setIdx(i => (i - 1 + photos.length) % photos.length);
  const goNext = () => setIdx(i => (i + 1) % photos.length);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl p-0 bg-black/95 border-0 overflow-hidden" style={{ zIndex: 100 }} data-testid="photo-lightbox">
        <DialogTitle className="sr-only">Photo {idx + 1} of {photos.length}</DialogTitle>
        <div className="relative flex items-center justify-center" style={{ minHeight: '70vh' }}>
          <img src={photos[idx]?.src} alt="" className="max-h-[80vh] max-w-full object-contain" />
          {photos.length > 1 && (
            <>
              <button onClick={goPrev} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors" data-testid="lightbox-prev">
                <ChevronLeft className="h-5 w-5 text-white" />
              </button>
              <button onClick={goNext} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors" data-testid="lightbox-next">
                <ChevronRight className="h-5 w-5 text-white" />
              </button>
            </>
          )}
          <div className="absolute top-3 right-3 bg-black/50 text-white rounded-full px-3 py-1 text-xs font-medium">{idx + 1} / {photos.length}</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/* ── Main Detail Page ──────────────────────────────────────────── */
export default function ListingDetailPage({ publicMode = false }) {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const moveIn = searchParams.get('move_in') || '';
  const moveOut = searchParams.get('move_out') || '';
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPhotoIdx, setCurrentPhotoIdx] = useState(0);
  // When a listing loads (or photos change), snap the main hero carousel
  // to the flagged cover photo. This way the user's chosen marketing
  // shot is what shows first — without moving it from its real position
  // in the gallery.
  const lastCoverSnapRef = useRef(null);
  useEffect(() => {
    const photos = listing?.photos || [];
    const coverIdx = photos.findIndex(p => p.is_cover);
    if (coverIdx < 0) return;
    const key = `${listing?.id}:${photos[coverIdx]?.id}`;
    if (lastCoverSnapRef.current === key) return;
    lastCoverSnapRef.current = key;
    setCurrentPhotoIdx(coverIdx);
  }, [listing]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);

  // Preload Fraunces serif in public mode so headings match the landing.
  useEffect(() => {
    if (!publicMode) return;
    if (document.getElementById('fraunces-font')) return;
    const link = document.createElement('link');
    link.id = 'fraunces-font';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Inter:wght@400;500;600&display=swap';
    document.head.appendChild(link);
  }, [publicMode]);

  // Admin is automatically available on the private domain because the
  // site-wide PasswordGate stashed the password as `app_password`. On the
  // public share domain (publicMode=true) all admin UI is hidden.
  const adminPass = publicMode ? '' : (typeof window !== 'undefined' ? sessionStorage.getItem('app_password') || '' : '');
  const adminUnlocked = !publicMode && !!adminPass;

  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editActiveSince, setEditActiveSince] = useState('');
  const [savingDetails, setSavingDetails] = useState(false);
  const [togglingListed, setTogglingListed] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  const [selectedMonths, setSelectedMonths] = useState([]);
  const [priceInput, setPriceInput] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);

  const [showAmenityPicker, setShowAmenityPicker] = useState(false);
  const [settingCover, setSettingCover] = useState(null);
  // Public-mode contact dialog. When opened, we tag the lead with this
  // listing's id+title so the inbox + email show which unit was contacted.
  const [showContact, setShowContact] = useState(false);

  // Drag reorder state
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [photoManagerOpen, setPhotoManagerOpen] = useState(false);

  // Public share footer contact block (phone / whatsapp / email / hours).
  // We mirror the same source the Collection share pages use so the two
  // stay in sync when admins edit the site-wide contact info.
  const [publicContact, setPublicContact] = useState(null);
  useEffect(() => {
    if (!publicMode) return;
    fetch(`${API}/api/public/site-contact`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => setPublicContact(d))
      .catch(() => {});
  }, [publicMode]);

  const fetchListing = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/public/listings/${id}`);
      if (!res.ok) throw new Error('Not found');
      const data = await res.json();
      setListing(data);
      setEditTitle(data.title);
      setEditDesc(data.description || '');
      setEditAddress(data.address || '');
      setEditActiveSince(data.active_since ? String(data.active_since).slice(0, 10) : '');
    } catch { toast.error('Failed to load listing'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchListing(); }, [fetchListing]);

  // Auto-geocode safety net: if an admin loads a listing that has an
  // address text but no lat/lng saved (i.e. an old listing that predates
  // the JS-Geocoder fix), silently resolve the coords and save them in
  // the background. Runs once per listing-load when conditions match.
  // Cleans up legacy listings without forcing the user to click into
  // Edit → Save on each one.
  const autoGeocodedRef = useRef(false);
  useEffect(() => {
    if (!adminUnlocked && !((typeof window !== 'undefined') && localStorage.getItem('app_token'))) return;
    if (!listing) return;
    if (!listing.address) return;
    if (listing.address_lat != null && listing.address_lng != null) return;
    if (autoGeocodedRef.current) return;
    autoGeocodedRef.current = true;
    (async () => {
      try {
        const geo = await geocodeAddress(listing.address);
        if (!geo) return;
        const token = (typeof window !== 'undefined') ? localStorage.getItem('app_token') : null;
        const res = await fetch(`${API}/api/public/admin/listings/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ password: adminPass, address: listing.address, address_lat: geo.lat, address_lng: geo.lng }),
        });
        if (res.ok) {
          toast.success('Map coordinates added automatically.');
          fetchListing();
        }
      } catch (err) {
        console.warn('[auto-geocode] failed:', err);
      }
    })();
  }, [listing, adminUnlocked, adminPass, fetchListing, id]);

  // Breadcrumb flag: tells the /listings page that an "All Listings" back nav
  // should resume the previous date search (rather than clearing it on a
  // hard refresh). Consumed + cleared by ListingsPage on its next mount.
  useEffect(() => {
    if (moveIn && moveOut) {
      sessionStorage.setItem('listings_resume_search', '1');
    }
  }, [moveIn, moveOut]);

  const photos = useMemo(() => {
    if (listing?.photos?.length > 0)
      return listing.photos.map(p => ({ ...p, src: p.url.startsWith('http') ? p.url : `${API}${p.url}` }));
    return PLACEHOLDER_IMAGES.map((url, i) => ({ id: `ph-${i}`, src: url, filename: 'Placeholder', isPlaceholder: true }));
  }, [listing]);

  const openLightbox = (idx) => { setLightboxIdx(idx); setLightboxOpen(true); };

  const handleSaveDetails = async () => {
    setSavingDetails(true);
    try {
      let lat = listing?.address_lat;
      let lng = listing?.address_lng;
      const addressChanged = editAddress && editAddress !== listing?.address;
      const missingCoords = lat == null || lng == null;
      // Re-geocode whenever (a) the address text changed, OR (b) we have an
      // address but no lat/lng yet (covers the "I typed the address but the
      // map still says not set" case for brand-new listings).
      if (editAddress && (addressChanged || missingCoords)) {
        try {
          const geo = await geocodeAddress(editAddress);
          if (geo) {
            lat = geo.lat;
            lng = geo.lng;
          } else {
            toast.error("Couldn't find that address on the map. Save will keep your address text but the map won't render.");
            lat = null;
            lng = null;
          }
        } catch (err) {
          console.error('[geocode] failed:', err);
          toast.error(`Map lookup failed: ${err?.message || 'unknown error'}. Saving address only.`);
          lat = null;
          lng = null;
        }
      }
      const token = (typeof window !== 'undefined') ? localStorage.getItem('app_token') : null;
      const res = await fetch(`${API}/api/public/admin/listings/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ password: adminPass, title: editTitle, description: editDesc, address: editAddress, address_lat: lat, address_lng: lng, active_since: editActiveSince || null })
      });
      if (res.ok) { toast.success('Listing updated'); setEditing(false); fetchListing(); }
      else toast.error('Failed to update');
    } catch { toast.error('Failed to update'); }
    finally { setSavingDetails(false); }
  };

  const handleToggleListed = async () => {
    // Flip is_listed (true → unlisted, false/undefined → listed). When a
    // listing is unlisted it disappears from /public/listings + vacancy
    // search but stays editable here. Accepts EITHER the master app
    // password (legacy gate) OR the dashboard JWT admin role — Bearer
    // header below covers the multi-user admins who don't know the
    // master password.
    setTogglingListed(true);
    try {
      const next = listing?.is_listed === false ? true : false;
      const token = (typeof window !== 'undefined') ? localStorage.getItem('app_token') : null;
      const res = await fetch(`${API}/api/public/admin/listings/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ password: adminPass, is_listed: next })
      });
      if (res.ok) {
        toast.success(next ? 'Listing reactivated — visible on marketplace.' : 'Listing deactivated — hidden from marketplace.');
        fetchListing();
      } else if (res.status === 401) {
        toast.error('Admin access required to toggle listings.');
      } else {
        toast.error('Could not change listing status');
      }
    } catch {
      toast.error('Could not change listing status');
    } finally {
      setTogglingListed(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      if (files.length === 1) {
        const formData = new FormData(); formData.append('file', files[0]);
        const res = await fetch(`${API}/api/public/admin/listings/${id}/photos?password=${encodeURIComponent(adminPass)}`, { method: 'POST', body: formData });
        if (res.ok) {
          const newPhoto = await res.json();
          // Append the new photo to local state — no full refetch needed so
          // the Edit dialog's scroll position stays put.
          setListing(prev => ({
            ...prev,
            photos: [...(prev?.photos || []), { ...newPhoto, is_cover: false, is_deleted: false }],
          }));
          toast.success('Photo uploaded');
        } else { toast.error('Upload failed'); }
      } else {
        const formData = new FormData(); files.forEach(f => formData.append('files', f));
        const res = await fetch(`${API}/api/public/admin/listings/${id}/photos/batch?password=${encodeURIComponent(adminPass)}`, { method: 'POST', body: formData });
        if (res.ok) {
          const data = await res.json();
          setListing(prev => ({
            ...prev,
            photos: [
              ...(prev?.photos || []),
              ...((data.uploaded || []).map(p => ({ ...p, is_cover: false, is_deleted: false }))),
            ],
          }));
          toast.success(`${data.count} photos uploaded`);
        } else { toast.error('Upload failed'); }
      }
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handlePhotoDelete = async (photoId) => {
    try {
      const res = await fetch(`${API}/api/public/admin/listings/${id}/photos/delete`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: adminPass, photo_id: photoId }) });
      if (res.ok) {
        toast.success('Photo removed');
        // Optimistic local removal — keeps the dialog's scroll position
        // (full refetch was causing the page/dialog to jump to top after
        // every photo edit).
        setListing(prev => ({
          ...prev,
          photos: (prev?.photos || []).filter(p => p.id !== photoId),
        }));
        setCurrentPhotoIdx(idx => Math.max(0, Math.min(idx, ((listing?.photos?.length || 1) - 2))));
      }
    } catch { toast.error('Failed to delete'); }
  };

  const handleSetCover = async (photoId) => {
    setSettingCover(photoId);
    try {
      const res = await fetch(`${API}/api/public/admin/listings/${id}/photos/cover`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: adminPass, photo_id: photoId }) });
      if (res.ok) {
        toast.success('Cover photo set');
        // Mark only this photo as cover — preserve order so the chosen
        // photo stays in its current position (e.g. picture 5 stays at
        // position 5, just now also flagged as the cover).
        setListing(prev => ({
          ...prev,
          photos: (prev?.photos || []).map(p => ({ ...p, is_cover: p.id === photoId })),
        }));
      } else { toast.error('Failed to set cover'); }
    } catch { toast.error('Failed to set cover'); }
    finally { setSettingCover(null); }
  };

  // Drag-and-drop reorder
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDrop = async (dropIdx) => {
    if (dragIdx === null || dragIdx === dropIdx) { setDragIdx(null); setDragOverIdx(null); return; }
    const currentPhotos = listing?.photos || [];
    const newOrder = [...currentPhotos];
    const [moved] = newOrder.splice(dragIdx, 1);
    newOrder.splice(dropIdx, 0, moved);
    const photoIds = newOrder.map(p => p.id);
    setDragIdx(null); setDragOverIdx(null);
    // Optimistically apply the new order locally — no full refetch so
    // the Edit dialog's scroll position is preserved.
    setListing(prev => ({ ...prev, photos: newOrder }));
    try {
      const res = await fetch(`${API}/api/public/admin/listings/${id}/photos/reorder`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: adminPass, photo_ids: photoIds }) });
      if (!res.ok) {
        toast.error('Failed to reorder');
        // Roll back on failure
        setListing(prev => ({ ...prev, photos: currentPhotos }));
      }
    } catch {
      toast.error('Failed to reorder');
      setListing(prev => ({ ...prev, photos: currentPhotos }));
    }
  };

  // Reorder + auto-sync cover for the Manage Photos dialog. Whichever
  // photo ends up at index 0 becomes the cover — the rest of the app
  // (public listing, lightbox, hero) already keys off `is_cover` so we
  // just need to fire the existing endpoints in sequence.
  const handleManagerReorder = async (newOrder) => {
    const currentPhotos = listing?.photos || [];
    const photoIds = newOrder.map(p => p.id);
    setListing(prev => ({ ...prev, photos: newOrder }));
    try {
      const res = await fetch(`${API}/api/public/admin/listings/${id}/photos/reorder`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: adminPass, photo_ids: photoIds }) });
      if (!res.ok) {
        toast.error('Failed to reorder');
        setListing(prev => ({ ...prev, photos: currentPhotos }));
        return;
      }
      // Auto-sync the cover: if the new top tile isn't the current cover,
      // promote it. Optimistic flip in local state matches what the
      // set-cover endpoint will eventually persist.
      const newTop = newOrder[0];
      if (newTop && !newTop.is_cover) {
        setListing(prev => ({
          ...prev,
          photos: (prev?.photos || []).map(p => ({ ...p, is_cover: p.id === newTop.id })),
        }));
        try {
          await fetch(`${API}/api/public/admin/listings/${id}/photos/cover`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: adminPass, photo_id: newTop.id }) });
        } catch {
          // Reorder still succeeded — cover sync failure is rare; surface
          // it gently and let the user manually retry if needed.
          toast.error('Reorder saved, but cover sync failed');
        }
      }
    } catch {
      toast.error('Failed to reorder');
      setListing(prev => ({ ...prev, photos: currentPhotos }));
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const MAX_VIDEO_MB = 200;
    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > MAX_VIDEO_MB) {
      toast.error(`Video is ${sizeMb.toFixed(1)} MB — please upload a smaller file (under ${MAX_VIDEO_MB} MB).`);
      if (videoInputRef.current) videoInputRef.current.value = '';
      return;
    }
    setUploadingVideo(true);
    try {
      const formData = new FormData(); formData.append('file', file);
      const res = await fetch(`${API}/api/public/admin/listings/${id}/video?password=${encodeURIComponent(adminPass)}`, { method: 'POST', body: formData });
      if (res.ok) { toast.success('Video uploaded'); fetchListing(); }
      else {
        let detail = '';
        try { const j = await res.json(); detail = j?.detail || ''; } catch { try { detail = await res.text(); } catch { /* noop */ } }
        if (res.status === 413) toast.error('Video is too large for the server proxy — try a smaller file or compress it first.');
        else toast.error(`Video upload failed (${res.status})${detail ? `: ${detail}` : ''}`);
        console.error('Video upload failed', res.status, detail);
      }
    } catch (err) {
      console.error('Video upload error', err);
      toast.error(`Video upload failed: ${err?.message || 'network error'}`);
    }
    finally { setUploadingVideo(false); if (videoInputRef.current) videoInputRef.current.value = ''; }
  };

  const handleVideoDelete = async () => {
    try {
      const res = await fetch(`${API}/api/public/admin/listings/${id}/video/delete`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: adminPass }) });
      if (res.ok) { toast.success('Video removed'); fetchListing(); }
    } catch { toast.error('Failed to delete video'); }
  };

  const handleSaveAmenities = async (amenities) => {
    try {
      const res = await fetch(`${API}/api/public/admin/listings/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: adminPass, amenities }) });
      if (res.ok) { toast.success('Amenities updated'); fetchListing(); } else toast.error('Failed to save amenities');
    } catch { toast.error('Failed to save amenities'); }
  };

  const toggleMonth = (year, month) => {
    const key = `${year}-${month}`;
    setSelectedMonths(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleSetPrice = async () => {
    if (selectedMonths.length === 0 || !priceInput) { toast.error('Select months and enter a price'); return; }
    setSavingPrice(true);
    const entries = selectedMonths.map(key => { const [y, m] = key.split('-').map(Number); return { year: y, month: m, price: parseFloat(priceInput) }; });
    try {
      const res = await fetch(`${API}/api/public/admin/pricing`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: adminPass, unit_id: id, entries }) });
      if (res.ok) { toast.success(`Price set for ${selectedMonths.length} month(s)`); setSelectedMonths([]); setPriceInput(''); fetchListing(); }
    } catch { toast.error('Failed to save'); }
    finally { setSavingPrice(false); }
  };

  const handleDeletePrice = async (year, month) => {
    try {
      const res = await fetch(`${API}/api/public/admin/pricing/delete`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: adminPass, unit_id: id, year, month }) });
      if (res.ok) { toast.success('Price removed'); fetchListing(); }
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (!listing) return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
      <p className="text-muted-foreground text-lg">Listing not found</p>
      <Link to={publicMode ? '/booknow' : '/listings'}><Button variant="outline"><ArrowLeft className="h-4 w-4 mr-2" />See All Listings</Button></Link>
    </div>
  );

  const videoUrl = listing.video?.url ? (listing.video.url.startsWith('http') ? listing.video.url : `${API}${listing.video.url}`) : null;
  const listingsIndexPath = publicMode ? '/booknow' : '/listings';
  const backUrl = moveIn && moveOut ? `${listingsIndexPath}?start=${moveIn}&end=${moveOut}` : listingsIndexPath;

  return (
    <div
      className={`min-h-screen ${publicMode ? 'bg-background flex flex-col' : 'bg-background'}`}
      data-testid="listing-detail-page"
    >
      {publicMode ? (
        // Public share detail page — reuse the same emerald Collection
        // header + ContactFooter so a bare /listings/{id} share link
        // looks identical to a collection listing detail. `business_heading`
        // falls back to "Flex Living" when no contact/company name is set.
        <CollectionHeader business_heading="Flex Living" />
      ) : (
        <header className={`sticky top-0 z-50 backdrop-blur-md border-b bg-white/95 border-border/60`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <Link to={listingsIndexPath} className="font-heading text-xl font-bold text-primary tracking-tight">Flex Living Miami</Link>
            <div className="flex items-center gap-2">
              <Link to={backUrl} data-testid="see-all-listings-link">
                <Button variant="outline" size="sm"><ArrowLeft className="h-4 w-4 mr-1" />All Listings</Button>
              </Link>
              {adminUnlocked && (
                <>
                  {listing?.is_listed === false && (
                    <span
                      className="text-[10px] uppercase tracking-wider font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-1 rounded-full"
                      data-testid="listing-unlisted-badge"
                    >
                      Unlisted
                    </span>
                  )}
                  <Button
                    variant="outline" size="sm"
                    onClick={handleToggleListed}
                    disabled={togglingListed}
                    className={listing?.is_listed === false ? 'border-emerald-500 text-emerald-700 hover:bg-emerald-50' : 'border-amber-500 text-amber-700 hover:bg-amber-50'}
                    data-testid="toggle-listed-button"
                  >
                    {togglingListed ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                    {listing?.is_listed === false ? 'List' : 'Unlist'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </header>
      )}

      {publicMode && (
        // Slim breadcrumb sits *inside* the main content column (like the
        // Collection listing page) so the emerald header stays clean.
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-4">
          <Link to={backUrl} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="see-all-listings-link">
            <ArrowLeft className="h-4 w-4" /> Back to all listings
          </Link>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* ─── Two-column layout: Photos+Map (left) stretch to match Details (right) ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          {/* ── Left Column: Photo Gallery (top) + Map (fills remaining height) ── */}
          <div className="flex flex-col gap-6" data-testid="left-column">
            <div className="relative flex-shrink-0" data-testid="image-carousel">
              <div className={`aspect-[4/3] overflow-hidden bg-muted relative cursor-pointer ${publicMode ? 'rounded-3xl border border-[#D7E0D5]' : 'rounded-xl'}`} onClick={() => openLightbox(currentPhotoIdx)}>
                <img src={photos[currentPhotoIdx]?.src} alt={listing.title} className="w-full h-full object-cover" decoding="async" />
                {photos.length > 1 && (
                  <>
                    <button onClick={(e) => { e.stopPropagation(); setCurrentPhotoIdx(i => (i - 1 + photos.length) % photos.length); }}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center shadow-md hover:bg-white transition-colors" data-testid="carousel-prev">
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setCurrentPhotoIdx(i => (i + 1) % photos.length); }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center shadow-md hover:bg-white transition-colors" data-testid="carousel-next">
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </>
                )}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {photos.slice(0, 10).map((_, i) => (
                    <button key={i} onClick={(e) => { e.stopPropagation(); setCurrentPhotoIdx(i); }}
                      className={`w-2.5 h-2.5 rounded-full transition-colors ${i === currentPhotoIdx ? 'bg-white shadow' : 'bg-white/50'}`} />
                  ))}
                  {photos.length > 10 && <span className="text-white text-xs ml-1">+{photos.length - 10}</span>}
                </div>
                <div className="absolute top-3 right-3 bg-black/50 text-white rounded-full px-2.5 py-0.5 text-xs font-medium">{currentPhotoIdx + 1} / {photos.length}</div>
                {photos[currentPhotoIdx]?.is_cover && (
                  <div className="absolute top-3 left-3 bg-yellow-500/90 text-white rounded-full px-2.5 py-0.5 text-xs font-medium flex items-center gap-1"><Star className="h-3 w-3" /> Cover</div>
                )}
              </div>
              {/* Thumbnail strip */}
              {photos.length > 1 && (
                <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
                  {photos.map((photo, i) => (
                    <button key={photo.id} onClick={() => openLightbox(i)}
                      className={`relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${i === currentPhotoIdx ? 'border-primary' : 'border-transparent hover:border-border'}`}>
                      <img src={photo.src} alt="" className="w-full h-full object-cover" loading="lazy" />
                      {photo.is_cover && <div className="absolute inset-0 flex items-center justify-center bg-yellow-500/30"><Star className="h-3 w-3 text-yellow-600" /></div>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Map — stretches to fill remaining column height */}
            <div className="flex flex-col flex-1 min-h-[320px]" data-testid="map-column">
              <h3 className="font-heading text-lg font-semibold mb-3 flex items-center gap-1.5 flex-shrink-0"><MapPin className="h-5 w-5 text-primary" />Location</h3>
              {listing.address && listing.address_lat && listing.address_lng ? (
                <div className="rounded-xl overflow-hidden border border-border/70 flex-1" style={{ position: 'relative', zIndex: 0 }} data-testid="location-map">
                  <GoogleMapView lat={listing.address_lat} lng={listing.address_lng} zoom={13} title={listing.address} />
                </div>
              ) : (
                <div className="w-full flex-1 rounded-xl border border-dashed border-border/70 bg-muted/30 flex items-center justify-center text-sm text-muted-foreground min-h-[320px]">
                  <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />No map location set</span>
                </div>
              )}
            </div>
          </div>

          {/* ── Right Column: Unit Info + Description ── */}
          <div className="space-y-5" data-testid="listing-info">
            {(listing.bedrooms != null || listing.bathrooms != null) && (
              <div className="flex items-center gap-3 text-muted-foreground">
                {listing.bedrooms != null && (
                  <span className="flex items-center gap-1.5 bg-muted rounded-full px-3 py-1 text-xs font-medium">
                    <BedDouble className="h-3.5 w-3.5" />{listing.bedrooms === 0 ? 'Studio' : `${listing.bedrooms} Bed`}
                  </span>
                )}
                {listing.bathrooms != null && (
                  <span className="flex items-center gap-1.5 bg-muted rounded-full px-3 py-1 text-xs font-medium">
                    <Bath className="h-3.5 w-3.5" />{listing.bathrooms} Bath
                  </span>
                )}
                {publicMode && <NextAvailableChip next={listing.available_from} />}
              </div>
            )}
            <h1
              className={publicMode
                ? "font-heading text-2xl sm:text-3xl font-bold tracking-tight"
                : "font-heading text-2xl sm:text-3xl font-bold tracking-tight"}
              data-testid="detail-title"
            >{listing.title}</h1>
            {listing.address && (
              <p className="text-muted-foreground flex items-center gap-1.5"><MapPin className="h-4 w-4" />{listing.address}</p>
            )}
            {!publicMode && listing.available_from && (
              <p className="inline-flex items-center gap-1.5 text-sm font-medium rounded-full px-3 py-1 text-emerald-700 bg-emerald-50 border border-emerald-200" data-testid="detail-available-from">
                <CalendarDays className="h-3.5 w-3.5" />
                Available from {new Date(listing.available_from + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            )}
            {listing.current_price != null ? (
              <p className="text-primary font-bold text-2xl tabular-nums" data-testid="detail-price">${listing.current_price.toLocaleString()}/mo</p>
            ) : (
              <p className="text-muted-foreground">Contact for pricing</p>
            )}

            {publicMode && (
              <Button
                onClick={() => setShowContact(true)}
                className="bg-emerald-700 hover:bg-emerald-800 text-white shadow-sm gap-1.5"
                data-testid="detail-contact-cta"
              >
                <Mail className="h-4 w-4 mr-1.5" />
                Contact us about this listing
              </Button>
            )}

            {listing.description && <DescriptionDisplay html={listing.description} />}
          </div>
        </div>

        {/* ─── Amenities — full width, compact pills ─── */}
        {listing.amenities?.length > 0 && (
          <div className="mt-10" data-testid="amenities-section">
            <h3 className="font-heading text-lg font-semibold mb-4 text-center">Amenities</h3>
            <div className="flex flex-wrap gap-2 justify-center">
              {listing.amenities.map((amenity, i) => (
                <div key={amenity.name || i} className="inline-flex items-center gap-1.5 bg-muted/60 rounded-full px-3 py-1.5 text-xs">
                  <AmenityIcon icon={amenity.icon} className="h-3.5 w-3.5 text-foreground/80" />
                  <span>{amenity.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ─── Video Section ─────────────────────────────────── */}
        {videoUrl && (
          <div className="mt-8" data-testid="video-section">
            <h3 className="font-heading text-lg font-semibold mb-3">Property Video</h3>
            <div className="rounded-xl overflow-hidden border border-border/70 bg-black max-w-3xl">
              <video controls className="w-full" preload="metadata" style={{ maxHeight: '500px' }}>
                <source src={videoUrl} type={listing.video?.content_type || 'video/mp4'} />
              </video>
            </div>
          </div>
        )}
      </div>

      {/* Photo Lightbox */}
      <PhotoLightbox open={lightboxOpen} onOpenChange={setLightboxOpen} photos={photos} startIdx={lightboxIdx} />

      {/* Public-share contact footer — mirrors Collection listing page */}
      {publicMode && <ContactFooter contact={publicContact} />}

      {/* ─── Admin Dialog — no password prompt; site-wide gate already verified ── */}
      <Dialog open={showAdminDialog} onOpenChange={setShowAdminDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" style={{ zIndex: 60 }} data-testid="detail-admin-section">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg flex items-center gap-2">
              <Pencil className="h-5 w-5 text-primary" />Edit Listing
            </DialogTitle>
            <DialogDescription className="sr-only">Edit this listing's details, amenities, photos, video, and pricing.</DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
              {/* Edit Details */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-sm flex items-center gap-1.5">
                    <Pencil className="h-4 w-4 text-primary" />Details
                  </h4>
                  {!editing && (
                    <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-details-button"><Pencil className="h-3 w-3 mr-1" />Edit</Button>
                  )}
                </div>
                {editing ? (
                  <div className="space-y-3">
                    <div><Label className="text-sm mb-1 block">Title</Label><Input value={editTitle} onChange={e => setEditTitle(e.target.value)} data-testid="edit-title-input" /></div>
                    <div>
                      <Label className="text-sm mb-1 block">Description <span className="text-muted-foreground font-normal">(supports bold text & paragraphs)</span></Label>
                      <RichTextEditor value={editDesc} onChange={setEditDesc} />
                    </div>
                    <div>
                      <Label className="text-sm mb-1 block">Address</Label>
                      <Input value={editAddress} onChange={e => setEditAddress(e.target.value)} placeholder="e.g., 123 Ocean Dr, Miami Beach, FL 33139" data-testid="edit-address-input" />
                      <p className="text-xs text-muted-foreground mt-1">Address will be geocoded for map display</p>
                    </div>
                    <div>
                      <Label className="text-sm mb-1 block">Active since <span className="text-muted-foreground font-normal">(counts towards YTD occupancy from this date)</span></Label>
                      <Input type="date" value={editActiveSince} onChange={e => setEditActiveSince(e.target.value)} data-testid="edit-active-since-input" />
                      <p className="text-xs text-muted-foreground mt-1">Leave empty to use the unit's original creation date.</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleSaveDetails} disabled={savingDetails} data-testid="save-details-button">
                        {savingDetails ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setEditing(false); setEditTitle(listing.title); setEditDesc(listing.description || ''); setEditAddress(listing.address || ''); setEditActiveSince(listing.active_since ? String(listing.active_since).slice(0, 10) : ''); }}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p><strong>Title:</strong> {listing.title}</p>
                    {listing.description && <div><strong>Description:</strong> <DescriptionDisplay html={listing.description} /></div>}
                    {listing.address && <p><strong>Address:</strong> {listing.address}</p>}
                    {listing.active_since && <p><strong>Active since:</strong> {String(listing.active_since).slice(0, 10)}</p>}
                  </div>
                )}
              </div>
              <hr />

              {/* Amenities */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-sm flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-primary" />Amenities ({listing.amenities?.length || 0})</h4>
                  <Button variant="outline" size="sm" onClick={() => setShowAmenityPicker(true)} data-testid="manage-amenities-button"><Plus className="h-3 w-3 mr-1" />Manage</Button>
                </div>
                {listing.amenities?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {listing.amenities.map((a, i) => (
                      <div key={a.name || i} className="flex items-center gap-1.5 bg-muted rounded-full px-3 py-1.5 text-xs">
                        <AmenityIcon icon={a.icon} className="h-3.5 w-3.5" /><span>{a.name}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground">No amenities added yet.</p>}
              </div>
              <hr />

              {/* Photos — preview strip + "Manage photos" opens the big grid dialog */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-sm flex items-center gap-1.5"><Image className="h-4 w-4 text-primary" />Photos ({listing.photos?.length || 0})</h4>
                  <div className="flex items-center gap-2">
                    <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" />
                    <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-photo-button">
                      {uploading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Upload className="h-3 w-3 mr-1" />}Upload
                    </Button>
                    <Button
                      variant="default" size="sm"
                      onClick={() => setPhotoManagerOpen(true)}
                      disabled={(listing.photos?.length || 0) === 0}
                      data-testid="open-photo-manager-btn"
                    >
                      <Image className="h-3 w-3 mr-1" /> Manage photos
                    </Button>
                  </div>
                </div>
                {listing.photos?.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => setPhotoManagerOpen(true)}
                    className="w-full grid gap-2 p-2 rounded-lg border bg-card hover:bg-muted/30 transition-colors text-left"
                    style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))' }}
                    data-testid="photo-strip"
                  >
                    {listing.photos.slice(0, 12).map((photo, idx) => (
                      <div
                        key={photo.id}
                        className="relative rounded-md overflow-hidden bg-muted"
                        style={{ aspectRatio: '3 / 4' }}
                      >
                        <img
                          src={`${API}${photo.url}`}
                          alt={photo.filename}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        {idx === 0 && (
                          <span className="absolute top-1 left-1 bg-white/95 text-[#1f2933] text-[9px] font-medium px-1.5 py-0.5 rounded-full shadow-sm">
                            Cover
                          </span>
                        )}
                      </div>
                    ))}
                    {listing.photos.length > 12 && (
                      <div
                        className="rounded-md bg-muted flex items-center justify-center text-xs text-muted-foreground"
                        style={{ aspectRatio: '3 / 4' }}
                      >
                        +{listing.photos.length - 12}
                      </div>
                    )}
                  </button>
                ) : <p className="text-sm text-muted-foreground">No photos uploaded yet. Click <span className="font-medium">Upload</span> to add some.</p>}
                <p className="text-[10px] text-muted-foreground">
                  Click any photo to open the photo manager — drag tiles to reorder, the top-left becomes the cover.
                </p>
              </div>
              <hr />

              {/* Video */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-sm flex items-center gap-1.5"><Video className="h-4 w-4 text-primary" />Video</h4>
                  {!listing.video && (
                    <div>
                      <input ref={videoInputRef} type="file" accept="video/*" onChange={handleVideoUpload} className="hidden" />
                      <Button variant="outline" size="sm" onClick={() => videoInputRef.current?.click()} disabled={uploadingVideo} data-testid="upload-video-button">
                        {uploadingVideo ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Upload className="h-3 w-3 mr-1" />}Upload Video
                      </Button>
                    </div>
                  )}
                </div>
                {listing.video ? (
                  <div className="flex items-center gap-3 p-2 rounded-lg border border-border/70 bg-card">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0 flex items-center justify-center"><Video className="h-6 w-6 text-muted-foreground" /></div>
                    <div className="flex-1 min-w-0"><p className="text-xs text-muted-foreground truncate">{listing.video.filename}</p></div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={handleVideoDelete} title="Delete video"><Trash2 className="h-3.5 w-3.5" /></Button>
                      <div>
                        <input ref={videoInputRef} type="file" accept="video/*" onChange={handleVideoUpload} className="hidden" />
                        <Button variant="ghost" size="sm" className="h-7 text-xs px-2" onClick={() => videoInputRef.current?.click()} disabled={uploadingVideo}>
                          {uploadingVideo ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Replace'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : <p className="text-sm text-muted-foreground">No video uploaded.</p>}
              </div>
              <hr />

              {/* Pricing */}
              <div className="space-y-3">
                <h4 className="font-medium text-sm flex items-center gap-1.5"><Pencil className="h-4 w-4 text-primary" />Pricing</h4>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1.5 block">Select Months ({selectedMonths.length} selected)</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {getNext18Months().map(({ year, month }) => {
                      const key = `${year}-${month}`;
                      const sel = selectedMonths.includes(key);
                      return (
                        <button key={key} onClick={() => toggleMonth(year, month)} data-testid={`detail-month-chip-${month}-${year}`}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${sel ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-foreground border-border hover:bg-muted'}`}>
                          {MONTH_NAMES[month - 1].slice(0, 3)} '{String(year).slice(2)}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="flex gap-3 items-end">
                  <div className="flex-1 max-w-[180px]">
                    <Label className="text-sm mb-1 block">Price</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <Input type="number" placeholder="0" className="pl-7" value={priceInput} onChange={e => setPriceInput(e.target.value)} data-testid="detail-price-input" />
                    </div>
                  </div>
                  <Button size="sm" onClick={handleSetPrice} disabled={savingPrice || selectedMonths.length === 0} data-testid="detail-set-price-button">
                    {savingPrice ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                    Set Price{selectedMonths.length > 0 ? ` for ${selectedMonths.length} mo` : ''}
                  </Button>
                </div>
                {listing.pricing?.length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1.5 block">Current Pricing</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {listing.pricing.sort((a, b) => a.year - b.year || a.month - b.month).map(p => (
                        <div key={`${p.year}-${p.month}`} className="flex items-center gap-1.5 bg-muted rounded-full px-2.5 py-1 text-xs">
                          <span className="font-medium">{MONTH_NAMES[p.month - 1].slice(0, 3)} {p.year}</span>
                          <span className="text-primary font-semibold">${p.price.toLocaleString()}</span>
                          <button onClick={() => handleDeletePrice(p.year, p.month)} className="text-destructive hover:text-destructive/80 ml-0.5"><X className="h-3 w-3" /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
        </DialogContent>
      </Dialog>

      <AmenityPicker open={showAmenityPicker} onOpenChange={setShowAmenityPicker} currentAmenities={listing.amenities || []} onSave={handleSaveAmenities} adminPass={adminUnlocked ? adminPass : ''} />

      <PhotoManagerDialog
        open={photoManagerOpen}
        onOpenChange={setPhotoManagerOpen}
        photos={listing?.photos || []}
        uploading={uploading}
        onUpload={handlePhotoUpload}
        onDelete={handlePhotoDelete}
        onReorder={handleManagerReorder}
        onPreview={(idx) => { setPhotoManagerOpen(false); openLightbox(idx); }}
        apiBase={API}
      />

      <LeadCaptureForm
        mode="inline"
        listing={{ id: listing.id, title: listing.title }}
        open={showContact}
        onOpenChange={setShowContact}
      />

      <footer className="border-t bg-card py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-muted-foreground">&copy; {new Date().getFullYear()} Flex Living. All rights reserved.</p>
        </div>
      </footer>
      <Toaster position="bottom-right" />
    </div>
  );
}
